import { DRAGON, SKILL_CASTS, SKILLS, glyphTurns, lookOf as skillLook, type SkillId } from '@somnhold/content';
import { hexLen, hexToWorld, worldToHex } from '../hex.ts';
import { idiv, imax, imin, withinRange } from '../math/fixed.ts';
import type { BattleState, Hero } from '../types.ts';
import { laneLit, onGlass, stuckNow } from '../battle/dragon.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill, kitOf } from '../battle/skills.ts';

/**
 * Spectres in the dragon's clearing (docs/UNICAS.md §4.11): they fight with what they carry and as their players
 * do, and only know a few simple fixed things. Off the fury's glass, out of a marked burn; a spectre within reach of
 * a free harpoon goes for its lever when its lane lights, but only with another harpoon already stuck in the dragon
 * (the first one is always someone live's: otherwise two spectres would bring it down by themselves); and never fire
 * at the dragon (its own skills of fire, those a glyph turned to fire, its turrets of fire).
 */

/** Whether a skill on that button deals fire (its own type, or a conversion glyph's). */
export const firesSkill = (h: Hero, slot: 0 | 1 | 2): boolean => {
  const id = kitOf(h)[slot];
  if (!id) return false;
  // What the cast really is: a conversion glyph first, then its variant (the artificer's fire turrets, the embers),
  // then the skill itself.
  const turned = glyphTurns(id, h.glyphs?.[slot]);
  if (turned) return turned === 'fire';
  return skillLook(id, h.forks?.[slot] ?? null) === 'fire' || SKILL_CASTS[id].type === 'fire';
};

/** A skill of fire aimed near the dragon (at it, or a point within `near` of it): what a spectre doesn't do. */
export const fireOnDragon = (s: BattleState, h: Hero, slot: 0 | 1 | 2, x: number | undefined, y: number | undefined, id: number | undefined, near = 3000): boolean => {
  if (!s.dragon || !firesSkill(h, slot)) return false;
  // A turret stays and turns to whatever comes near: a fire one is never planted in the dragon's fight.
  if (kitOf(h)[slot] === 'turret') return true;
  const d = s.enemies.find((e) => e.id === s.dragon?.id && e.hp > 0);
  if (!d) return false;
  if (id === d.id) return true;
  return withinRange(x ?? h.x, y ?? h.y, d.x, d.y, near);
};

/** A step toward the middle, off the glass's rim (one hex of margin inside its edge). */
const offGlass = (s: BattleState, h: Hero): boolean => {
  const st = s.dragon;
  if (!st || st.phase !== 'fury') return false;
  const at = worldToHex(h.x, h.y);
  if (hexLen(at.q, at.r) < st.glass && !onGlass(s, h.x, h.y)) return false;
  const k = hexLen(at.q, at.r) || 1;
  const keep = st.glass > 1 ? st.glass - 1 : 0;
  const to = hexToWorld(idiv(at.q * keep, k), idiv(at.r * keep, k));
  if (!h.moving || s.tick % 6 === 0) orderMove(s, h, to.x, to.y);
  h.target = 0;
  return true;
};

/** Out of a burn about to go off (its breath, its sweep). */
const offMarks = (s: BattleState, h: Hero): boolean => {
  for (const z of s.zones) {
    if (z.kind !== 'eruption' || (z.warn ?? -1) <= 0 || !withinRange(h.x, h.y, z.x, z.y, z.radius + 150)) continue;
    let dx = h.x - z.x;
    const dy = h.y - z.y;
    if (dx === 0 && dy === 0) dx = 1;
    const d = imax(1, (dx < 0 ? -dx : dx) + (dy < 0 ? -dy : dy));
    const out = z.radius + 500;
    if (!h.moving || s.tick % 6 === 0) orderMove(s, h, z.x + idiv(dx * out, d), z.y + idiv(dy * out, d));
    h.target = 0;
    return true;
  }
  return false;
};

/** The lever a spectre runs for now (-1: none): a lit, free lane near it, with a harpoon already in the dragon. */
const leverFor = (s: BattleState, h: Hero, first: boolean): number => {
  const list = s.rules?.dragon?.harpoons ?? [];
  if (!first && stuckNow(s) < 1) return -1;
  for (let i = 0; i < list.length; i++) {
    const p = list[i] as (typeof list)[number];
    if (!laneLit(s, i) || !withinRange(h.x, h.y, p.lx, p.ly, DRAGON.harpoons.spectre)) continue;
    // Someone else (standing nearer) already on it.
    const mine = h.x - p.lx;
    const me = mine * mine + (h.y - p.ly) * (h.y - p.ly);
    if (s.heroes.some((o) => o.id !== h.id && o.downTicks === 0 && (o.x - p.lx) * (o.x - p.lx) + (o.y - p.ly) * (o.y - p.ly) < me)) continue;
    return i;
  }
  return -1;
};

/** Runs for a lever and holds it. */
const pull = (s: BattleState, h: Hero, i: number): void => {
  const p = (s.rules?.dragon?.harpoons ?? [])[i];
  if (!p) return;
  if (withinRange(h.x, h.y, p.lx, p.ly, DRAGON.harpoons.reach - 150)) {
    h.moving = false;
    h.path = [];
  } else if (!h.moving || s.tick % 6 === 0) orderMove(s, h, p.lx, p.ly);
  h.target = 0;
};

/** A spectre's own moves in the dragon's clearing (true: it did one this tick instead of fighting). */
export const dragonGhost = (s: BattleState, h: Hero): boolean => {
  if (!s.rules?.dragon || h.control !== 'ghost') return false;
  if (offGlass(s, h) || offMarks(s, h)) return true;
  const i = leverFor(s, h, false);
  if (i < 0) return false;
  pull(s, h, i);
  return true;
};

/* ---------- the balance bot that knows the rules (docs/UNICAS.md §4.10) ---------- */

/** What a skill on that button deals (its own type, or a conversion glyph's). */
const typeOf = (h: Hero, slot: 0 | 1 | 2): string => {
  const id = kitOf(h)[slot];
  return id ? (glyphTurns(id, h.glyphs?.[slot]) ?? SKILL_CASTS[id].type ?? 'physical') : 'physical';
};

/** Whether that button stuns (a dry blow on the dragon's glass). */
const stunsSkill = (h: Hero, slot: 0 | 1 | 2): boolean => {
  const id = kitOf(h)[slot];
  return !!id && (SKILLS[id].stun ?? 0) > 0;
};

/** The loaded harpoons in the order the dragon comes to their lanes. */
const upcoming = (s: BattleState): number[] => {
  const st = s.dragon;
  const list = s.rules?.dragon?.harpoons ?? [];
  if (!st) return [];
  return list
    .map((p, i) => ({ i, d: (((p.arc - st.arc) % 8192) + 8192) % 8192 }))
    .filter((x) => (st.ready[x.i] ?? 0) <= s.tick + 12 * 4)
    .sort((a, b) => a.d - b.d || a.i - b.i)
    .map((x) => x.i);
};

/**
 * Who goes to the harpoons in the sky: the hero nearest the next lever ahead of it, and with more than one, the one
 * nearest the lever after that (the slowed dragon reaches it while the first holds). Whoever fired runs on to the next.
 */
const duties = (s: BattleState): Map<number, number> => {
  const out = new Map<number, number>();
  if (s.dragon?.phase !== 'sky') return out;
  const list = s.rules?.dragon?.harpoons ?? [];
  const up = s.heroes.filter((h) => h.downTicks === 0 && !h.pid.startsWith('npc:') && !(s.rules?.dragon?.spectres ?? []).includes(h.pid));
  const next = upcoming(s).slice(0, imin(2, up.length));
  for (const i of next) {
    const p = list[i] as (typeof list)[number];
    let best: Hero | null = null;
    let bd = 0;
    for (const h of up) {
      if (out.has(h.id)) continue;
      const d = (h.x - p.lx) * (h.x - p.lx) + (h.y - p.ly) * (h.y - p.ly);
      if (!best || d < bd) {
        best = h;
        bd = d;
      }
    }
    if (best) out.set(best.id, i);
  }
  return out;
};

/**
 * A balance bot that plays by the rules (§4.10): to the harpoons when it's its turn (the first one too), off the glass
 * and the burns, frost on the dragon until the glass is full and a stun on it once brittle, never fire; otherwise it
 * fights as a spectre does. True when it moved for the dragon this tick (the spectre's own thinking is skipped).
 */
export const dragonBot = (s: BattleState, h: Hero, plan = duties(s)): boolean => {
  if (!s.rules?.dragon || h.downTicks > 0) return false;
  if (offGlass(s, h) || offMarks(s, h)) return true;
  const i = plan.get(h.id);
  if (i !== undefined) {
    pull(s, h, i);
    return true;
  }
  const d = s.enemies.find((e) => e.id === s.dragon?.id && e.hp > 0);
  if (!d || h.stun > 0) return false;
  const brittleNow = s.dragon?.phase === 'down' || (s.dragon?.cold ?? 0) >= (s.rules.dragon.ask ?? 1);
  for (const slot of [0, 1, 2] as const) {
    if (h.skillCd[slot] > 0) continue;
    const sk = SKILLS[kitOf(h)[slot] as SkillId];
    if (!withinRange(h.x, h.y, d.x, d.y, imax(sk.castRange, 1500))) continue;
    const t = typeOf(h, slot);
    const want = (t === 'frost' && !brittleNow) || (brittleNow && (stunsSkill(h, slot) || t === 'lightning'));
    if (!want || (slot === 2 && t !== 'frost')) continue;
    castSkill(s, h, { t: 'skill', slot, x: d.x, y: d.y, id: d.id });
  }
  return false;
};

/** The harpoon duties this tick, for `dragonBot` (worked out once for every bot). */
export const dragonDuties = duties;
